import { useMemo, useCallback, memo, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { HeaderContainer } from '@/components/organisms/Header';
import { SidebarContainer, type SidebarSection } from '@/components/organisms/Sidebar';
import { BookOpen, NotebookPen, LayoutDashboard, Square, MessageCircle, Users, GraduationCap, ShieldCheck, Compass } from 'lucide-react';
import { logger } from '@/lib/logger';
import { useSidebarContext } from '@/contexts/useSidebarContext';

import { cn } from '@/lib/utils';
import { LAYOUT, REMEMBERED_USER_KEY, COURSE_WORKSPACE_ROUTE } from '@/lib/constants';
import type { RememberedUser } from '@/lib/types';
import { useConcurrentNavigation } from '@/hooks/useConcurrentNavigation';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { useProfileData } from '@/hooks/useProfileData';
import { AppLayoutSkeleton } from '@/components/templates/skeletons';
import { NavigationProgressBar } from '@/components/atoms/NavigationProgressBar';
import { KeyboardShortcutCheatsheet } from '@/components/molecules/KeyboardShortcutCheatsheet';
import { useShortcut } from '@/hooks/useShortcut';
import { useGlobalConnectionRealtime } from '@/hooks/useGlobalConnectionRealtime';
import { useNotificationRealtime } from '@/hooks/useNotificationRealtime';
import { useReducedMotion } from '@/hooks/useReducedMotion';

export const PersistentLayout = memo(function PersistentLayout() {
  // === ALL HOOKS MUST BE CALLED UNCONDITIONALLY BEFORE ANY EARLY RETURN ===
  // This ensures React Rules of Hooks compliance - hooks called in same order every render
  
  // 1. Router hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { navigate: navigateConcurrent, isNavigating: isTransitionPending } = useConcurrentNavigation();
  
  // 2. Context hooks
  const { isCollapsed, hoveredItemId, toggleSidebar, handleMouseEnter, handleMouseLeave } = useSidebarContext();
  const { signOut, loading, user } = useAuth();
  // Step 61 (PR-A11Y.E): drop the workspace-exit slide-in chrome animations
  // when the user requests reduced motion. The CSS `*!important` rule loses
  // to `@layer utilities`-scoped Tailwind animation utilities (duration-200
  // wins the cascade), so the component itself must opt out at className-time.
  const prefersReducedMotion = useReducedMotion();
  
  // 3. Data fetching hooks (handles undefined userId internally via enabled flag)
  const { data: profileData } = useProfileData(user?.id);

  // R7.5 focus-on-route-mount (Step 33 — app-wide extension of Step 26's CourseLayout pattern).
  // On route change, move keyboard focus to `<main id="main-content">` so arrow keys + PageDown
  // work on the first keystroke. Skips if the user is mid-keystroke in any input / textarea /
  // contenteditable / iframe (same-origin GeoGebra embeds) — protects the tutor composer,
  // search bars, and any future typing surface from having focus yanked during navigation.
  //
  // Dep list includes `user?.id` because on cold load `<main>` doesn't mount until after
  // `useAuth()` resolves (line 176's `if (!user)` gate renders `AppLayoutSkeleton` instead).
  // Without the user dep, the first-mount effect runs against a null ref, and a subsequent
  // user-resolve re-render doesn't fire the effect because `location.pathname` hasn't
  // changed — focus never transfers. Triggering on `user?.id` catches this cold-load path.
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      const ae = document.activeElement as HTMLElement | null;
      if (!ae) {
        el.focus({ preventScroll: true });
        return;
      }
      if (el.contains(ae)) return;
      if (
        ae instanceof HTMLInputElement ||
        ae instanceof HTMLTextAreaElement ||
        ae.isContentEditable ||
        ae.tagName === 'IFRAME'
      ) {
        return;
      }
      el.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [location.pathname, user?.id]);
  
  // 4. Global realtime subscriptions (enabled only when user exists - internal check)
  useGlobalConnectionRealtime();
  useNotificationRealtime(profileData?.username);
  
  // 4b. Role check for conditional sidebar items
  const { isAdmin, isCourseAdmin, isSuperAdmin } = useUserRole();

  // 4c. Global keyboard shortcuts (Linear-style sequences)
  useShortcut('g h', () => navigateConcurrent('/dashboard'), {
    description: 'Go to Dashboard', category: 'Navigation',
  });
  useShortcut('g c', () => navigateConcurrent('/classroom'), {
    description: 'Go to Classroom', category: 'Navigation',
  });

  // 5. useCallback hooks
  const handleNavigation = useCallback((path: string) => {
    navigateConcurrent(path);
  }, [navigateConcurrent]);

  const handleAvatarClick = useCallback(() => {
    if (profileData?.username) {
      navigateConcurrent(`/profile/${profileData.username}`);
    } else {
      navigateConcurrent('/profile/user');
    }
  }, [navigateConcurrent, profileData?.username]);

  const handleSignOut = useCallback(async () => {
    if (loading) return;
    
    try {
      // Persist cosmetic profile data for remembered-user sign-in screen
      if (user?.email && profileData) {
        const remembered: RememberedUser = {
          firstName: profileData.first_name ?? '',
          lastName: profileData.last_name ?? '',
          email: user.email,
          avatarUrl: profileData.avatar_url ?? null,
        };
        localStorage.setItem(REMEMBERED_USER_KEY, JSON.stringify(remembered));
      }

      logger.debug({ feature: "sign-out-initiated" });
      const { error } = await signOut();
      
      if (error) {
        logger.error('Sign out error:', error);
        toast.error("There was an error signing you out. Please try again.");
        return;
      }
      
      logger.debug({ feature: "sign-out-success" });
      navigate('/');
    } catch (error) {
      logger.error('Unexpected sign out error:', error);
      toast.error("An unexpected error occurred. Please try again.");
    }
  }, [signOut, loading, navigate, user?.email, profileData]);

  // 7. useMemo hooks
  const sidebarSections: SidebarSection[] = useMemo(() => {
    if (!user) {
      return [];
    }
    
    const isCompleteProfile = location.pathname === '/complete-profile';
    
    if (isCompleteProfile) {
      return [];
    }

    const hasAnyAdminRole = isAdmin || isCourseAdmin || isSuperAdmin;
    
    const sections: SidebarSection[] = [
      {
        title: "Study",
        items: [
          { icon: BookOpen, label: "Classroom", onClick: () => handleNavigation('/classroom'), route: '/classroom' },
          { icon: NotebookPen, label: "Notebook", onClick: () => handleNavigation('/notebook'), route: '/notebook' },
          { icon: LayoutDashboard, label: "Dashboard", onClick: () => handleNavigation('/dashboard'), route: '/dashboard' },
          { icon: Compass, label: "Explore", onClick: () => handleNavigation('/explore'), route: '/explore' },
        ]
      },
      {
        title: "Social", 
        items: [
          { icon: Square, label: "Square", onClick: () => handleNavigation('/square'), route: '/square' },
          { icon: MessageCircle, label: "Messages", onClick: () => handleNavigation('/messages'), route: '/messages' },
        ]
      },
    ];

    if (hasAnyAdminRole) {
      sections.push({
        title: "Ctrl",
        items: [
          ...(isAdmin ? [{ icon: Users, label: "User", onClick: () => handleNavigation('/user'), route: '/user' }] : []),
          ...(isCourseAdmin ? [{ icon: GraduationCap, label: "Course", onClick: () => handleNavigation('/course'), route: '/course' }] : []),
          ...(isSuperAdmin ? [{ icon: ShieldCheck, label: "Super Admin", onClick: () => handleNavigation('/super-admin'), route: '/super-admin' }] : []),
        ]
      });
    }

    return sections;
  }, [handleNavigation, location.pathname, user, isAdmin, isCourseAdmin, isSuperAdmin]);

  // === CONDITIONAL RETURN IS SAFE NOW - ALL HOOKS CALLED ABOVE ===
  if (!user) {
    return <AppLayoutSkeleton />;
  }

  // === NON-HOOK CODE BELOW ===
  const isCompleteProfile = location.pathname === '/complete-profile';
  const isWorkspaceMode = COURSE_WORKSPACE_ROUTE.test(location.pathname);
  const showChrome = !isCompleteProfile && !isWorkspaceMode;

  return (
    <div className="min-h-screen w-full bg-background relative">
      {/* GAP-98: WCAG 2.1 SC 2.4.1 — MUST be first focusable element so
          a Tab from page-load lands here. Placing it after Header/Sidebar
          would defeat the purpose entirely. sr-only by default; becomes
          visible top-left when keyboard-focused. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>
      <NavigationProgressBar isNavigating={isTransitionPending} />
      <KeyboardShortcutCheatsheet />
      {/* Step 25 (R7.6): workspace-exit slide-in. When the user leaves a
          course workspace route (COURSE_WORKSPACE_ROUTE flips false),
          `showChrome` flips true and these two elements mount. The
          `animate-in slide-in-from-*` classes fire once on mount, so the
          header drops in from the top and the sidebar enters from the left
          in 200ms (R7.6 spec). Classes attach directly to the components
          (not a wrapper) because both use `fixed`/`lg:fixed` positioning,
          and a transform on a parent would create a new containing block
          and break that positioning. First-visit loads outside any
          workspace also get the same graceful entry. Reduced motion is
          globally zero'd in index.css §GAP-65. */}
      {showChrome && (
        <HeaderContainer
          firstName={profileData?.first_name}
          lastName={profileData?.last_name}
          username={profileData?.username}
          avatarUrl={profileData?.avatar_url}
          onAvatarClick={handleAvatarClick}
          onSettingsClick={() => handleNavigation('/settings')}
          onSignOut={handleSignOut}
          sidebarWidth={0}
          className={prefersReducedMotion ? undefined : 'animate-in slide-in-from-top-full duration-200'}
        />
      )}

      {showChrome && (
        <SidebarContainer
          sections={sidebarSections}
          isCollapsed={isCollapsed}
          hoveredItemId={hoveredItemId}
          isNavigating={isTransitionPending}
          activeRoute={location.pathname}
          onToggle={toggleSidebar}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={prefersReducedMotion ? undefined : 'animate-in slide-in-from-left-full duration-200'}
        />
      )}

      <main
        ref={mainRef}
        id="main-content"
        tabIndex={-1}
        className={cn("relative transition-all duration-300 ease-in-out", {
          "pt-24": showChrome
        })}
        style={{
          marginLeft: showChrome && typeof window !== 'undefined' && window.innerWidth >= LAYOUT.MOBILE_BREAKPOINT
            ? isCollapsed ? LAYOUT.SIDEBAR_WIDTH_COLLAPSED : LAYOUT.SIDEBAR_WIDTH_EXPANDED
            : 0
        }}
      >
        <div className="w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
});